import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder } from '@angular/forms';
import { Observable, of, switchMap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationsService } from 'angular2-notifications';
import { MODIFY_EVENT_STEPS_ID } from '../constants';
import { CreateEventDisciplinesForm, CreateEventForm } from '../types';
import { EventResponseEvent } from '../../../types/event-response.type';
import {
  EventCreateRequest,
  EventModifyPricing,
} from '../../../types/event-request.type';
import { EventsHttpService } from '../../../services/events.http.service';
import { UtilsService } from '../../../services/utils.service';
import { Option } from '../../inputs/select/options';
import { DateTimeService } from '../../../services/date-time.service';
import { TranslateService } from '../../../services/translate.service';

@Component({
  selector: 'app-create-event',
  templateUrl: './create-event.component.html',
  styleUrls: ['./create-event.component.scss'],
})
export class CreateEventComponent implements OnInit {
  protected readonly Object = Object;
  protected readonly MODIFY_EVENT_STEP_ID = MODIFY_EVENT_STEPS_ID;

  private event_country_id: number | undefined;

  public BASIC_DETAILS_STEPS = {
    BASIC_INFO: {
      id: 'basic-info',
      title: 'event_management_basic_info',
    },
    SPORTS_AND_DISCIPLINES: {
      id: 'sport-and-disciplines',
      title: 'event_management_sport_and_disciplines',
    },
    LOCATION: {
      id: 'location',
      title: 'event_management_location',
    },
  };

  public activeBasicDetailsStep: string =
    this.BASIC_DETAILS_STEPS.BASIC_INFO.id;

  public fragmentChangeScrolling: boolean = false;

  public innerForm = new FormGroup<CreateEventForm>({
    event_id: new FormControl(null),
    event_status_id: new FormControl(null, {
      nonNullable: true,
    }),
    event_organization_id: new FormControl(null, {
      nonNullable: true,
    }),
    event_name: new FormControl('', {
      nonNullable: true,
    }),
    event_start_date: new FormControl('', {
      nonNullable: true,
    }),
    event_start_time: new FormControl('', {
      nonNullable: true,
    }),
    event_finish_date: new FormControl('', {
      nonNullable: true,
    }),
    event_finish_time: new FormControl('', {
      nonNullable: true,
    }),
    event_short_description: new FormControl('', {
      nonNullable: true,
    }),
    event_description: new FormControl('', {
      nonNullable: true,
    }),
    event_cover_photo: new FormControl(null),
    categories: new FormControl([], {
      nonNullable: true,
    }),
    event_location: new FormControl(
      { value: undefined, disabled: false },
      {
        nonNullable: true,
      }
    ),
    event_no_location: new FormControl(false, {
      nonNullable: true,
    }),

    event_registration_open: new FormControl(false, {
      nonNullable: true,
    }),
    event_registration_start_date: new FormControl('', {
      nonNullable: true,
    }),
    event_registration_start_time: new FormControl('', {
      nonNullable: true,
    }),
    event_registration_end_date: new FormControl('', {
      nonNullable: true,
    }),
    event_registration_end_time: new FormControl('', {
      nonNullable: true,
    }),
    event_Registration_is_internal: new FormControl(true, {
      nonNullable: true,
    }),
    external_link: new FormControl(null),

    event_pricing_is_free: new FormControl(false, {
      nonNullable: true,
    }),
    event_pricing_model: new FormControl(1, {
      nonNullable: true,
    }),
  });

  public disciplinesForm: FormGroup<CreateEventDisciplinesForm> = new FormGroup(
    {}
  );

  public event$: Observable<EventResponseEvent | null> = this.route.params.pipe(
    switchMap((data) => {
      const eventId: number | null = data['event_id'] ?? null;

      if (eventId) {
        return this.eventsHttpService.getEvent(eventId);
      }

      return of(null);
    })
  );

  public MODIFY_EVENT_STEPS: Option[] = [
    {
      id: MODIFY_EVENT_STEPS_ID.BASIC_DETAILS,
      itemName: 'modify_event_steps_basic_details',
    },
    {
      id: MODIFY_EVENT_STEPS_ID.REGISTRATION_DETAILS,
      itemName: 'modify_event_steps_registration_details',
    },
    {
      id: MODIFY_EVENT_STEPS_ID.PRICING_MODEL,
      itemName: 'modify_event_steps_pricing_model',
    },
    {
      id: MODIFY_EVENT_STEPS_ID.REGISTRATION_FORM,
      itemName: 'modify_event_steps_registration_form',
    },
  ];

  public activeModifyEventStep: string = MODIFY_EVENT_STEPS_ID.BASIC_DETAILS;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventsHttpService: EventsHttpService,
    private notifications: NotificationsService,
    private cdr: ChangeDetectorRef,
    private formBuilder: UntypedFormBuilder
  ) {}

  get eventId(): number | null {
    return this.innerForm.controls.event_id.value;
  }

  get eventManagementTitle(): string {
    return this.eventId
      ? 'event_management_edit_event'
      : 'event_management_create_event';
  }

  get eventManagementTitleDescription(): string {
    return this.eventId
      ? 'event_management_edit_event_description'
      : 'event_management_create_event_description';
  }

  addEventDisciplineFormControls(
    categoryId: string,
    event?: EventResponseEvent
  ): void {
    if (!this.disciplinesForm.controls[categoryId]) {
      this.disciplinesForm.addControl(
        categoryId,
        this.formBuilder.group({
          disciplines: new FormControl(event?.distances ?? []),
          groups: new FormControl(event?.groups ?? []),
          discipline_starting_price: new FormControl(0),
        })
      );
    }
  }

  ngOnInit(): void {
    this.event$.subscribe((event) => {
      if (!event) {
        return;
      }

      event?.categories.forEach((category) => {
        this.addEventDisciplineFormControls(
          String(category.category_id),
          event
        );
      });

      this.innerForm.patchValue({
        event_id: event.event_id,
        event_name: event.event_name,
        event_status_id: event.event_status.id,
        event_organization_id: event.organization.organization_id,
        event_start_date: DateTimeService.formatDate(event.event_start),
        event_start_time: DateTimeService.formatTimeFromDate(event.event_start),
        event_finish_date: DateTimeService.formatDate(event.event_finish),
        event_finish_time: DateTimeService.formatTimeFromDate(
          event.event_finish
        ),
        event_description: event.event_description,
        event_short_description: event.event_short_description,
        categories: event.categories.map((cat) => cat.category_id),
        event_location: UtilsService.parseLocation(event.event_location),
        event_registration_open: event.event_registration_open,
        event_registration_start_date: event.event_registration_start
          ? DateTimeService.formatDate(event.event_registration_start)
          : '',
        event_registration_start_time: event.event_registration_start
          ? DateTimeService.formatTimeFromDate(event.event_registration_start)
          : '',
        event_registration_end_date: event.event_registration_end
          ? DateTimeService.formatDate(event.event_registration_end)
          : '',
        event_registration_end_time: event.event_registration_end
          ? DateTimeService.formatTimeFromDate(event.event_registration_end)
          : '',
        event_cover_photo: event.files
          ?.reverse()
          .find((file) => file.name === 'logo')?.url,
      });

      this.event_country_id = event.event_country?.country_id;

      this.cdr.detectChanges();
    });

    this.route.queryParams.subscribe(({ step }) => {
      if (this.isActiveModifyEventStep(step)) {
        this.activeModifyEventStep = step;
      }
    });

    this.innerForm.controls.categories.valueChanges.subscribe((categories) => {
      categories.forEach((categoryId) => {
        this.addEventDisciplineFormControls(String(categoryId));
      });
    });
  }

  public saveSection(): void {
    if (
      this.activeModifyEventStep === MODIFY_EVENT_STEPS_ID.BASIC_DETAILS ||
      this.activeModifyEventStep === MODIFY_EVENT_STEPS_ID.REGISTRATION_DETAILS
    ) {
      this.saveEvent();
    } else if (
      this.activeModifyEventStep === MODIFY_EVENT_STEPS_ID.PRICING_MODEL
    ) {
      // TODO save pricing model
    } else if (
      this.activeModifyEventStep === MODIFY_EVENT_STEPS_ID.REGISTRATION_FORM
    ) {
      // TODO save registration form
    }
  }

  private saveEvent(): void {
    Object.values(this.innerForm.controls).forEach((control: FormControl) => {
      control.markAsTouched();
      control.updateValueAndValidity();
    });

    if (this.innerForm.invalid) {
      this.notifications.warn(
        TranslateService.translate('warning'),
        TranslateService.translate('event_fill_required_fields')
      );
      return;
    }

    const {
      categories = [],
      event_description,
      event_short_description,
      event_finish_date = '',
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      event_status_id,
      event_registration_open,
      event_finish_time = '',
      event_location,
      event_start_date = '',
      event_start_time = '',
      event_organization_id,
      event_name,
      event_id,
      event_registration_start_date = '',
      event_registration_start_time = '',
      event_registration_end_date = '',
      event_registration_end_time = '',
    } = this.innerForm.value;

    // TODO: enable when BE start supporting the params
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const pricing: EventModifyPricing[] = [];
    let distances: number[] = [];
    let groups: number[] = [];
    /* eslint-enable @typescript-eslint/no-unused-vars */

    categories.forEach((categoryId) => {
      distances =
        this.disciplinesForm.controls[categoryId]?.controls.disciplines.value ??
        [];

      groups =
        this.disciplinesForm.controls[categoryId]?.controls.groups.value ?? [];
    });

    const eventBody: EventCreateRequest = {
      categories,
      // distances, // TODO
      // pricing, // TODO
      // groups, // TODO
      event_status_id: String(event_status_id),
      event_description: event_description ?? '',
      event_short_description: event_short_description ?? '',
      event_start: DateTimeService.displayDateToSqlDate(
        event_start_date ?? '',
        event_start_time
      ),
      event_registration_start: DateTimeService.displayDateToSqlDate(
        event_registration_start_date,
        event_registration_start_time
      ),
      event_location,
      event_name: event_name ?? '',
      event_finish: DateTimeService.displayDateToSqlDate(
        event_finish_date,
        event_finish_time
      ),
      event_registration_end: DateTimeService.displayDateToSqlDate(
        event_registration_end_date,
        event_registration_end_time
      ),
      event_registration_open: event_registration_open ?? false,
      event_organization_id: event_organization_id ?? 0,
    };

    this.eventsHttpService
      .saveUpdateEvent(eventBody, event_organization_id ?? 0, Number(event_id))
      .subscribe((res) => {
        const file = this.innerForm.get('event_cover_photo')?.value;

        if (file instanceof File) {
          this.eventsHttpService
            .updateLogo(file, res.event.event_id)
            .subscribe(() => {
              this.saveSuccessNotification(event_name ?? '');
            });
        } else {
          this.saveSuccessNotification(event_name ?? '');
        }
      });
  }

  saveSuccessNotification(eventName: string): void {
    this.notifications.success(
      TranslateService.translate('success'),
      `Event ${eventName} was successfully created/updated`,
      true
    );
    this.goToNextStep();
  }

  private goToNextStep(): void {
    let useNextStep = false;

    const nextStep = Object.values(MODIFY_EVENT_STEPS_ID).find((value) => {
      if (useNextStep) {
        return true;
      }

      useNextStep = value === this.activeModifyEventStep;
      return false;
    });

    if (nextStep) {
      this.changeActiveModifyEventStep(nextStep);
    }
  }

  public deleteEvent(): void {
    if (!this.eventId) {
      return;
    }

    this.eventsHttpService.deleteEvent(this.eventId).subscribe(() => {
      this.notifications.success(
        TranslateService.translate('success'),
        TranslateService.translate('event_was_deleted')
      );
    });
  }

  private isActiveModifyEventStep(step: string): boolean {
    return Boolean(this.MODIFY_EVENT_STEPS.find((val) => val.id === step)?.id);
  }

  public changeActiveModifyEventStep(step: string): void {
    if (this.activeModifyEventStep === step) {
      return;
    }

    if (this.eventId) {
      this.activeModifyEventStep = '';
    } else {
      step = MODIFY_EVENT_STEPS_ID.BASIC_DETAILS;
      this.activeModifyEventStep = step;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        step,
      },
      queryParamsHandling: 'merge',
    });
  }
}
